import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {
    avisosDaTroca,
    efeitoDaTroca,
    efeitoDoCancelamento,
    getMeioLabel,
    mesAtual,
    validarDesconto
} from "../assinaturasUtils";

const reais = (valor) => new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(valor) || 0);

// A casca dos diálogos: o erro da API fica dentro do diálogo, e ele só fecha quando a ação dá certo.
function Acao({aberto, titulo, rotulo, perigo, ocupado, erro, podeConfirmar = true, onFechar, onConfirmar, children}) {
    return (
        <Dialog open={aberto} onClose={ocupado ? undefined : onFechar} fullWidth maxWidth="sm">
            <DialogTitle>{titulo}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    {children}
                    {erro ? <Alert severity="error">{erro}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onFechar} disabled={ocupado}>Voltar</Button>
                <Button variant="contained" color={perigo ? 'error' : 'primary'} disabled={ocupado || !podeConfirmar} onClick={onConfirmar}>
                    {ocupado ? 'Aguarde…' : rotulo}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function ConfirmarDialog({aberto, titulo, texto, rotulo, perigo, ocupado, erro, onFechar, onConfirmar}) {
    return (
        <Acao aberto={aberto} titulo={titulo} rotulo={rotulo} perigo={perigo} ocupado={ocupado} erro={erro} onFechar={onFechar} onConfirmar={onConfirmar}>
            <Typography variant="body2">{texto}</Typography>
        </Acao>
    );
}

/*
 * Desconto sobre a mensalidade, percentual ou em reais, com mês final e motivo (BBF-106). O mesmo diálogo dá e
 * altera. O motivo aparece na fatura.
 */
export function DescontoDialog({aberto, licenca, ocupado, erro, onFechar, onConfirmar}) {
    const mensalidade = Number(licenca?.versao?.preco_base_mensal) || 0;
    const inicial = () => ({
        tipo: licenca?.desconto?.tipo || 'PERCENTUAL',
        valor: licenca?.desconto ? String(licenca.desconto.valor) : '',
        ate: licenca?.desconto?.ate || mesAtual(),
        motivo: licenca?.desconto?.motivo || '',
    });
    const [form, setForm] = useState(inicial);

    useEffect(() => {
        if (aberto) setForm(inicial());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto]);

    const problema = validarDesconto(form, mensalidade);
    const valor = Number(String(form.valor).replace(',', '.')) || 0;
    const abatimento = form.tipo === 'PERCENTUAL' ? mensalidade * valor / 100 : Math.min(valor, mensalidade);
    const mudar = (campo) => (event) => setForm((atual) => ({...atual, [campo]: event.target.value}));

    return (
        <Acao
            aberto={aberto}
            titulo={licenca?.desconto ? 'Alterar o desconto' : 'Dar desconto'}
            rotulo="Salvar o desconto"
            ocupado={ocupado}
            erro={erro}
            podeConfirmar={!problema}
            onFechar={onFechar}
            onConfirmar={() => onConfirmar({tipo: form.tipo, valor, ate: form.ate, motivo: form.motivo.trim()})}
        >
            <Typography variant="body2" color="text.secondary">
                Vale sobre a mensalidade da versão ({reais(mensalidade)}), não sobre opcionais. Entra na próxima fatura e sai sozinho depois do mês final.
            </Typography>
            <RadioGroup row value={form.tipo} onChange={mudar('tipo')}>
                <FormControlLabel value="PERCENTUAL" control={<Radio size="small" />} label="Percentual" />
                <FormControlLabel value="VALOR" control={<Radio size="small" />} label="Em reais" />
            </RadioGroup>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 2}}>
                <TextField label="Valor" type="number" size="small" value={form.valor} onChange={mudar('valor')}
                           inputProps={{min: 0, step: form.tipo === 'PERCENTUAL' ? 1 : 10}}
                           InputProps={form.tipo === 'PERCENTUAL'
                               ? {endAdornment: <InputAdornment position="end">%</InputAdornment>}
                               : {startAdornment: <InputAdornment position="start">R$</InputAdornment>}} />
                <TextField label="Mês final" type="month" size="small" value={form.ate} onChange={mudar('ate')}
                           inputProps={{min: mesAtual()}} InputLabelProps={{shrink: true}} />
            </Box>
            <TextField label="Motivo" size="small" value={form.motivo} onChange={mudar('motivo')} inputProps={{maxLength: 200}}
                       helperText="Aparece na fatura, na linha do desconto." />
            {!problema ? (
                <Alert severity="info" icon={false}>
                    A mensalidade de {reais(mensalidade)} fica com {reais(abatimento)} de desconto, até {form.ate.split('-').reverse().join('/')}.
                </Alert>
            ) : <Typography variant="caption" color="text.secondary">{problema}</Typography>}
        </Acao>
    );
}

/*
 * Mudar de plano ou migrar de versão (BBF-135). A lista traz a versão à venda de cada plano do sistema; a tela diz,
 * antes de confirmar, se é upgrade (na hora) ou downgrade (na virada do ciclo).
 */
export function TrocaDePlanoDialog({aberto, licenca, destinos, carregando, diaFechamento, alvoInicial, ocupado, erro, onFechar, onConfirmar}) {
    const [alvoId, setAlvoId] = useState('');

    useEffect(() => {
        if (aberto) setAlvoId(alvoInicial || destinos?.[0]?.id || '');
    }, [aberto, alvoInicial, destinos]);

    const alvo = (destinos || []).find((item) => item.id === alvoId);
    const efeito = alvo ? efeitoDaTroca(licenca, alvo, diaFechamento) : null;
    const avisos = alvo ? avisosDaTroca(licenca, alvo) : [];

    return (
        <Acao
            aberto={aberto}
            titulo="Mudar de plano"
            rotulo={efeito ? (efeito.agora ? 'Trocar agora' : 'Agendar a troca') : 'Trocar'}
            ocupado={ocupado}
            erro={erro}
            podeConfirmar={Boolean(alvo)}
            onFechar={onFechar}
            onConfirmar={() => onConfirmar(alvo)}
        >
            <Typography variant="body2" color="text.secondary">
                Hoje: {licenca?.versao?.nome_plano || licenca?.plano?.nome_plano} v{licenca?.versao?.numero}, {reais(licenca?.versao?.preco_base_mensal)} por mês.
            </Typography>
            {carregando ? <CircularProgress size={20} /> : null}
            {!carregando && !(destinos || []).length ? (
                <Alert severity="info">Não há outra versão à venda neste sistema para onde levar a assinatura.</Alert>
            ) : null}
            {(destinos || []).length ? (
                <TextField select label="Para" size="small" value={alvoId} onChange={(event) => setAlvoId(event.target.value)}>
                    {destinos.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                            {item.nome_plano} v{item.numero}{item.mesmoPlano ? ' (versão à venda do mesmo plano)' : ''} · {reais(item.preco_base_mensal)}
                        </MenuItem>
                    ))}
                </TextField>
            ) : null}
            {efeito ? <Alert severity={efeito.agora ? 'info' : 'warning'}>{efeito.texto}</Alert> : null}
            {avisos.map((aviso) => <Typography key={aviso} variant="body2" color="text.secondary">{aviso}</Typography>)}
        </Acao>
    );
}

// Cancelar no fim do ciclo pago (BBF-136), com o motivo, que fica no histórico.
export function CancelamentoDialog({aberto, licenca, diaFechamento, ocupado, erro, onFechar, onConfirmar}) {
    const [motivo, setMotivo] = useState('');
    useEffect(() => {
        if (aberto) setMotivo('');
    }, [aberto]);
    const efeito = efeitoDoCancelamento(licenca, diaFechamento);

    return (
        <Acao
            aberto={aberto}
            titulo="Cancelar a assinatura"
            rotulo={efeito.agora ? 'Cancelar agora' : 'Agendar o cancelamento'}
            perigo
            ocupado={ocupado}
            erro={erro}
            podeConfirmar={Boolean(motivo.trim())}
            onFechar={onFechar}
            onConfirmar={() => onConfirmar(motivo.trim())}
        >
            <Alert severity={efeito.agora ? 'warning' : 'info'}>{efeito.texto}</Alert>
            <TextField label="Motivo" size="small" required value={motivo} onChange={(event) => setMotivo(event.target.value)}
                       inputProps={{maxLength: 500}} multiline minRows={2} helperText="Fica no histórico da assinatura, com quem pediu." />
        </Acao>
    );
}

// Trocar o meio entre os que a versão aceita (BBF-137). O cartão novo é cadastrado pela empresa no checkout.
export function MeioDialog({aberto, licenca, ocupado, erro, onFechar, onConfirmar}) {
    const aceitos = licenca?.versao?.meios_aceitos || [];
    const [meio, setMeio] = useState(licenca?.meio_pagamento || '');
    useEffect(() => {
        if (aberto) setMeio(licenca?.meio_pagamento || '');
    }, [aberto, licenca?.meio_pagamento]);

    return (
        <Acao
            aberto={aberto}
            titulo="Trocar o meio de pagamento"
            rotulo="Trocar o meio"
            ocupado={ocupado}
            erro={erro}
            podeConfirmar={Boolean(meio) && meio !== licenca?.meio_pagamento}
            onFechar={onFechar}
            onConfirmar={() => onConfirmar(meio)}
        >
            <Typography variant="body2" color="text.secondary">A versão {licenca?.versao?.numero} aceita {aceitos.map((item) => getMeioLabel(item).toLowerCase()).join(' e ')}. Vale a partir da próxima fatura.</Typography>
            <RadioGroup value={meio} onChange={(event) => setMeio(event.target.value)}>
                {aceitos.map((item) => <FormControlLabel key={item} value={item} control={<Radio size="small" />} label={getMeioLabel(item)} />)}
            </RadioGroup>
        </Acao>
    );
}
